import { css } from '@emotion/react';
import { type FC } from 'react';
import { Accordion } from '~/_shared/baseComponents/accordion';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import { MapPrivacySelectorComponent } from '~/_shared/components/mapPrivacySelector/mapPrivacySelector.component';
import { type PasswordOption } from '~/_shared/components/mapPrivacySelector/mapPrivacySelector.types';
import { type MapPrivacyRestrictions } from '~/_shared/components/mapPrivacySelector/useMapPrivacyRestrictions.hook';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { type MapPrivacyLevel } from '~/_shared/types/map';
import { useTranslation } from '~/_shared/utils/hooks';
import { type ShareMapEmailResult } from '~/map/map.repository';
import { MapNameSectionComponent } from '../basicInfo/mapNameSection';
import {
  type ShareMethod, ShareSectionComponent,
} from './sections/shareSection.component';
import {
  StartingMapViewSectionComponent, type StartingView,
} from './sections/startingMapViewSection.component';
import { ShareInputField } from './shareInputField.enum';

export enum ShareMapSection {
  PrivacyLevel = 'PrivacyLevel',
  StartingMap = 'StartingMap',
  Share = 'Share',
}

const modalContentStyle = css({
  padding: 0,
});

const accordionLabelStyle = css({
  textTransform: 'uppercase',
  fontSize: '16px',
});

const radioGroupStyle = css({
  gap: 30,
});

const radioItemStyle = css({
  flexDirection: 'column',
  flexWrap: 'nowrap', //the default wrap makes the element taller
  lineHeight: 1,
});

const accordionPanelStyle = css({
  padding: '30px 25px',
});

const accordionItemStyle = css({
  textTransform: 'uppercase',
});

type ShareMapProps = Readonly<{
  expandedSections: ReadonlySet<ShareMapSection>;
  fieldErrors: ReadonlyMap<ShareInputField, ReadonlyArray<string>>;
  globalError: string | null;
  initialPasswordOption?: PasswordOption;
  isPasswordProtected?: boolean;
  isCalledFromMap: boolean;
  isLoading: boolean;
  isOpen: boolean;
  isSnapshot: boolean;
  lockedSections: ReadonlySet<ShareMapSection>;
  mapName: string;
  mapPrivacyRestrictions: MapPrivacyRestrictions;
  preservePasswordDisabledReason?: string;
  privacyLevel: MapPrivacyLevel | null;
  shareId: string;
  shareMethod: ShareMethod | null;
  startingMap: StartingView | null;
  sendEmailResult: ShareMapEmailResult | null;

  onChangeShareMethod: (method: ShareMethod) => void;
  onChangeStartingMap: (map: StartingView) => void;
  onClearFieldError: (field: ShareInputField) => void;
  onClearGlobalError: () => void;
  onClose: () => void;
  onMapFormSubmit?: (mapName: string) => void;
  onPasswordOptionChange?: (option: PasswordOption) => void;
  onPrivacyLevelChange: (level: MapPrivacyLevel, password?: string) => void;
  onToggleSection: (section: ShareMapSection) => void;
  onSubmitSendEmail: (emails: ReadonlyArray<string>, optionalMessage: string) => void;
}>;

export const ShareMapComponent: FC<ShareMapProps> = (props) => {
  const [t] = useTranslation();

  return (
    <ModalComponent
      caption={t('Share Your Map')}
      contentStyle={modalContentStyle}
      isOpen={props.isOpen}
      onClose={props.onClose}
      additionalContent={props.isLoading && <OverlayLoaderComponent />}
    >
      {props.globalError && (
        <AlertComponent
          type="danger"
          onClose={props.onClearGlobalError}
        >
          {props.globalError}
        </AlertComponent>
      )}

      <MapNameSectionComponent
        isSnapshot={props.isSnapshot}
        name={props.mapName}
      />

      <Accordion>

        <Accordion.Item
          open={props.expandedSections.has(ShareMapSection.PrivacyLevel)}
          onToggle={() => props.onToggleSection(ShareMapSection.PrivacyLevel)}
        >
          <Accordion.ItemHeading css={accordionItemStyle}>
            <Accordion.StepNumber />
            {t('Select Privacy Level')}
          </Accordion.ItemHeading>
          <Accordion.Panel css={accordionPanelStyle}>
            <MapPrivacySelectorComponent
              disabledPrivacyLevels={props.mapPrivacyRestrictions.disabledPrivacyLevels}
              globalError={props.mapPrivacyRestrictions.warning}
              initialPasswordOption={props.initialPasswordOption}
              isPasswordProtected={props.isPasswordProtected}
              onChange={props.onPrivacyLevelChange}
              onClearPasswordError={() => props.onClearFieldError(ShareInputField.Password)}
              onPasswordOptionChange={props.onPasswordOptionChange}
              passwordError={props.fieldErrors.get(ShareInputField.Password)?.[0] ?? undefined}
              preservePasswordDisabledReason={props.preservePasswordDisabledReason}
              privacyLevel={props.privacyLevel}
              radioGroupStyle={radioGroupStyle}
            />
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item
          open={!props.lockedSections.has(ShareMapSection.StartingMap) && props.expandedSections.has(ShareMapSection.StartingMap)}
          isLocked={props.lockedSections.has(ShareMapSection.StartingMap)}
          onToggle={() => props.onToggleSection(ShareMapSection.StartingMap)}
        >
          <Accordion.ItemHeading css={accordionItemStyle}>
            <Accordion.StepNumber />
            {t('Select Starting Map View')}
          </Accordion.ItemHeading>
          <Accordion.Panel css={accordionPanelStyle}>
            <StartingMapViewSectionComponent
              accordionLabelStyle={accordionLabelStyle}
              isCalledFromMap={props.isCalledFromMap}
              key={`${props.isOpen}`}
              mapNameErrors={props.fieldErrors.get(ShareInputField.MapName) ?? null}
              onChange={props.onChangeStartingMap}
              onClearMapNameErrors={() => props.onClearFieldError(ShareInputField.MapName)}
              onMapNameFormSubmit={props.onMapFormSubmit}
              radioGroupStyle={radioGroupStyle}
              value={props.startingMap}
            />
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item
          open={!props.lockedSections.has(ShareMapSection.Share) && props.expandedSections.has(ShareMapSection.Share)}
          isLocked={props.lockedSections.has(ShareMapSection.Share)}
          onToggle={() => props.onToggleSection(ShareMapSection.Share)}
        >
          <Accordion.ItemHeading css={accordionItemStyle}>
            <Accordion.StepNumber />
            {t('Select Sharing Method')}
          </Accordion.ItemHeading>
          <Accordion.Panel css={accordionPanelStyle}>
            <ShareSectionComponent
              accordionLabelStyle={accordionLabelStyle}
              key={`${props.isOpen}`}
              onChange={props.onChangeShareMethod}
              onSubmitSendEmail={props.onSubmitSendEmail}
              radioGroupStyle={radioGroupStyle}
              radioItemStyle={radioItemStyle}
              sendEmailResult={props.sendEmailResult}
              shareId={props.shareId}
              value={props.shareMethod}
            />
          </Accordion.Panel>
        </Accordion.Item>

      </Accordion>
    </ModalComponent>
  );
};
